import React, { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { FileMetadata, FolderStructure, GenerateSignedLinkResponse } from '../../../shared/types';
import { SignedLinkModal } from './SignedLinkModal';
import { handleApiError } from '../utils/api';

interface SignedLinkGeneratorProps {
  selectedFiles: FileMetadata[];
  selectedFolders: FolderStructure[];
  className?: string;
}

export const SignedLinkGenerator: React.FC<SignedLinkGeneratorProps> = ({
  selectedFiles,
  selectedFolders,
  className = ''
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [signedLinkData, setSignedLinkData] = useState<GenerateSignedLinkResponse | null>(null);

  const generateSignedLinkMutation = useMutation({
    mutationFn: apiService.generateSignedLink,
    onSuccess: (data) => {
      setSignedLinkData(data);
      setIsModalOpen(true);
    },
    onError: (error) => {
      console.error('Failed to generate signed link:', error);
    }
  });

  const canGenerateLink = () => {
    // Can generate link for exactly one file OR exactly one folder
    const totalSelected = selectedFiles.length + selectedFolders.length;
    return totalSelected === 1;
  };

  const getSelectionDescription = () => {
    if (selectedFiles.length === 1) {
      return `file "${selectedFiles[0].fileName}"`;
    } else if (selectedFolders.length === 1) {
      return `folder "${selectedFolders[0].concertName}"`;
    } else if (selectedFiles.length + selectedFolders.length === 0) {
      return 'Select a file or folder first';
    } else {
      return 'Select only one file or folder';
    }
  };

  const handleGenerateLink = () => {
    if (!canGenerateLink()) return;

    if (selectedFiles.length === 1) {
      // Generate link for single file
      generateSignedLinkMutation.mutate({
        fileId: selectedFiles[0].fileId,
        expirationHours: 24 // Default to 24 hours
      });
    } else if (selectedFolders.length === 1) {
      // Generate link for folder
      generateSignedLinkMutation.mutate({
        folderPath: selectedFolders[0].folderPath,
        expirationHours: 24 // Default to 24 hours
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSignedLinkData(null);
  };

  return (
    <>
      <div className={className}>
        <button
          onClick={handleGenerateLink}
          disabled={!canGenerateLink() || generateSignedLinkMutation.isPending}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors ${
            canGenerateLink() && !generateSignedLinkMutation.isPending
              ? 'text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              : 'text-gray-400 bg-gray-100 cursor-not-allowed'
          }`}
          title={canGenerateLink() ? `Generate signed link for ${getSelectionDescription()}` : getSelectionDescription()}
        >
          {generateSignedLinkMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4 mr-2" />
          )}
          Generate Link
        </button>

        {/* Selection info */}
        <div className="mt-2 text-xs text-gray-500">
          {canGenerateLink() 
            ? `Ready to generate link for ${getSelectionDescription()}`
            : getSelectionDescription()
          }
        </div>

        {/* Error display */}
        {generateSignedLinkMutation.isError && (
          <div className="mt-2 text-xs text-red-600">
            Error: {handleApiError(generateSignedLinkMutation.error)}
          </div>
        )}
      </div>

      {/* Modal */}
      <SignedLinkModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        signedLinkData={signedLinkData}
        isLoading={generateSignedLinkMutation.isPending}
        error={generateSignedLinkMutation.isError ? handleApiError(generateSignedLinkMutation.error) : null}
      />
    </>
  );
};